import useGTM from "@elgorditosalsero/react-gtm-hook";

export const SESSION_IDLE_WARNING_SHOWN = "session_idle_warning_shown";
export const SESSION_IDLE_LOGOUT = "session_idle_logout";
export const SESSION_EXTENDED = "session_extended";
export const SESSION_TOKEN_RENEWED = "session_token_renewed";
export const SESSION_TOKEN_RENEWAL_FAILED = "session_token_renewal_failed";
export const SESSION_ENDED_BY_SERVER = "session_ended_by_server";

/**
 * Intentionally non-identifying: durations and thresholds only.
 * Nothing here may carry member data — see CLAUDE.md's PHI rule. `userId` is
 * already attached to the GTM session once by `useAuth`, so repeating
 * identity per event would be both redundant and a new PHI surface.
 */
export interface SessionEventPayload {
  /** The configured idle window, so a config change is visible in the data. */
  idleWindowMinutes?: number;
  /** Seconds of token life left when a renewal was attempted. */
  secondsRemaining?: number;
}

/*
 * Deliberately absent: the HTTP status that ended a session server-side.
 * HttpClient collapses 401 (unverifiable) and 407 (expired) into one
 * `expired` reason, so nothing downstream can populate it — and an interface
 * that advertises a field nobody sets is worse than not having it. If that
 * distinction is ever wanted in telemetry, thread the status through the
 * reason first, then add the field back.
 */

/**
 * Telemetry for the session control (PPI-1848).
 *
 * Without this, a renewal path that silently stops working is
 * indistinguishable from normal operation until members start calling
 * support — and once PPI-1848's backend half cuts the token TTL, renewal
 * stops being a once-every-two-days event and becomes something every active
 * member depends on continuously.
 *
 * The signal to alert on is the ratio
 * `session_token_renewal_failed / session_token_renewed`: near zero in a
 * healthy system, and it goes to one the moment the backend contract drifts.
 *
 * Shaped like the existing `useWalletAnalytics`, with one deliberate
 * departure — see `send`. That hook is unguarded; do not copy it verbatim.
 */
export function useSessionAnalytics() {
  const { sendDataToGTM } = useGTM();

  /**
   * Analytics must never be able to break the control it observes.
   *
   * GTM executes its container's tags synchronously inside dataLayer.push, so
   * a broken tag throws straight back into the caller — the PPI-1836 failure
   * that `useAuth.safeSendDataToGTM` exists to contain. These events fire
   * from inside the idle tick and the logout path, so an escape here could
   * leave a member signed in past their window: the security control failing
   * open because of an analytics tag.
   *
   * Swallowed rather than logged: there is nothing actionable to recover
   * from a third-party tag failure, and the payloads are not worth surfacing.
   */
  const send = (event: string, payload?: SessionEventPayload): void => {
    try {
      sendDataToGTM({ event, ...payload });
    } catch {
      // Intentionally ignored -- see above.
    }
  };

  return {
    trackIdleWarningShown: (payload?: SessionEventPayload) =>
      send(SESSION_IDLE_WARNING_SHOWN, payload),
    trackIdleLogout: (payload?: SessionEventPayload) =>
      send(SESSION_IDLE_LOGOUT, payload),
    trackSessionExtended: (payload?: SessionEventPayload) =>
      send(SESSION_EXTENDED, payload),
    trackTokenRenewed: (payload?: SessionEventPayload) =>
      send(SESSION_TOKEN_RENEWED, payload),
    trackTokenRenewalFailed: (payload?: SessionEventPayload) =>
      send(SESSION_TOKEN_RENEWAL_FAILED, payload),
    trackSessionEndedByServer: (payload?: SessionEventPayload) =>
      send(SESSION_ENDED_BY_SERVER, payload),
  };
}
